import { useRef, useState } from 'react';
import { Screen } from '../../app/Frame';
import { useWritable } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { cropToSquare, ACCEPT, LogoRejected } from '../../lib/logo';
import {
  fieldError, formErrors, maskPhone, normalizeSite,
  type FieldName, type OrgForm,
} from '../../lib/org';
import { Field, FormSkeleton, LoadFailed, SectionHead, StickySave, useSaveOrLeave } from '../org-parts';
import { useOrgDraft } from '../org-draft';
import { useAdminSettings, useSaveSettings } from '../api';

/**
 * Профиль организации: то, что клиент видит в шапке приложения, в подтверждении
 * записи и в сообщениях бота.
 *
 * Часового пояса и правил записи здесь нет намеренно: они отвечают на вопрос
 * «как организация работает», а не «как она выглядит», и живут в разделе
 * «Расписание и запись». Телефон организации стоит здесь, потому что уходит
 * клиенту; ник администратора — в разделе «Администраторы», потому что уходит
 * в служебные сообщения. Стоя рядом, эти два поля регулярно заполнялись наоборот.
 */
type Draft = Omit<OrgForm, 'tz'> & { logo_url: string };

export function OrgProfile() {
  const writable = useWritable();
  const toast = useToast();
  const data = useAdminSettings();
  const save = useSaveSettings();

  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [logoError, setLogoError] = useState<string | null>(null);
  const anchors = useRef<Partial<Record<FieldName, HTMLDivElement | null>>>({});

  const s = data.data?.settings;
  const { draft, dirty, patch, commit } = useOrgDraft<Draft>(s
    ? {
      // Телефон приводим к маске сразу: сохранённый в другом виде номер иначе
      // так и остаётся разнобоем, пока его случайно не откроют на правку.
      title: s.title, about: s.about, phone: maskPhone(s.phone), email: s.email, site: s.site,
      logo_url: s.logo_url,
    }
    : null);

  useScreen('Профиль организации', null);
  useSaveOrLeave(dirty, () => submit());

  if (data.isError) {
    return <Screen><LoadFailed busy={data.isFetching} onRetry={() => void data.refetch()} /></Screen>;
  }
  if (!draft) {
    return (
      <Screen>
        <FormSkeleton rows={5} />
      </Screen>
    );
  }

  const busy = save.isPending;
  const locked = !writable || busy;

  const clearError = (name: FieldName) => setErrors((prev) => {
    if (!prev[name]) return prev;
    const next = { ...prev };
    delete next[name];
    return next;
  });

  /** Проверка на `blur`: ругаться на каждую букву в недописанном адресе — шум. */
  const check = (name: FieldName, value: string) =>
    setErrors((prev) => ({ ...prev, [name]: fieldError(name, value) ?? undefined }));

  async function pickLogo(file: File | undefined) {
    if (!file) return;
    setLogoError(null);
    try {
      patch({ logo_url: await cropToSquare(file) });
    } catch (e) {
      setLogoError(e instanceof LogoRejected ? e.message : 'Не удалось прочитать картинку');
    }
  }

  /**
   * На сервер уходят только поля этого раздела. Ручка принимает частичную
   * правку (`PATCH /api/admin/settings`), поэтому часовой пояс, правила записи
   * и уведомления остаются такими, какие они на сервере, — даже если их
   * поменяли в соседней вкладке, пока здесь была открыта форма.
   *
   * Обещание возвращается наружу: сторож ухода с экрана ждёт ответа сервера,
   * прежде чем уводить, — иначе мутация уехала бы вместе с экраном.
   */
  function submit(): Promise<unknown> {
    if (!draft) return Promise.resolve();
    // Схему сайта дописываем до проверки: иначе форма ругается на то, что сама
    // же собиралась исправить.
    const clean: Draft = {
      ...draft,
      title: draft.title.trim(),
      about: draft.about.trim(),
      email: draft.email.trim(),
      site: normalizeSite(draft.site),
    };
    // Часовой пояс в этой форме не правится — берём сохранённый, чтобы общая
    // проверка полей не спотыкалась о поле, которого здесь нет.
    const found = formErrors({ ...clean, tz: s?.tz ?? '' });
    patch(clean);
    setErrors(found);

    const first = (Object.keys(found) as FieldName[])[0];
    if (first) {
      anchors.current[first]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return Promise.reject(new Error('VALIDATION'));
    }

    return save.mutateAsync(clean).then(
      () => {
        commit(clean);
        toast('Изменения сохранены', { ms: 2000 });
      },
      (e: unknown) => {
        // Правки остаются в форме при любом исходе: повторить попытку можно
        // той же кнопкой, ничего не набирая заново.
        if (e instanceof ApiError) {
          // Сервер, назвавший поле, подсвечивает его так же, как проверка на клиенте.
          const field = (e.details as { field?: FieldName } | undefined)?.field;
          if (field) {
            setErrors({ [field]: e.message });
            anchors.current[field]?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          } else {
            toast(e.message);
          }
        } else {
          toast('Не получилось сохранить');
        }
        throw e;
      },
    );
  }

  return (
    <Screen className="orgform sticky-save">
      <p className="lead">
        Профиль для клиентов: показываем в шапке приложения, в подтверждении записи и в сообщениях бота
      </p>

      <div className="logo-row">
        <span className="logo-preview">
          {draft.logo_url ? <img src={draft.logo_url} alt="Логотип организации" /> : <i>лого</i>}
        </span>
        <span className="logo-act">
          <span className="logo-buttons">
            <label className={`btn${locked ? ' is-off' : ''}`}>
              {draft.logo_url ? 'Изменить логотип' : 'Загрузить логотип'}
              <input
                type="file"
                accept={ACCEPT}
                disabled={locked}
                onChange={(e) => {
                  void pickLogo(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {draft.logo_url && !locked ? (
              <button className="btn ghost-danger" type="button" onClick={() => patch({ logo_url: '' })}>
                Удалить логотип
              </button>
            ) : null}
          </span>
          {logoError ? <span className="err">{logoError}</span> : null}
        </span>
      </div>

      <Field
        label="Название"
        error={errors.title}
        anchor={(el) => { anchors.current.title = el; }}
      >
        {(id, bad) => (
          <input
            id={id} type="text" value={draft.title} disabled={locked} aria-invalid={bad}
            onChange={(e) => { patch({ title: e.target.value }); clearError('title'); }}
            onBlur={(e) => check('title', e.target.value)}
          />
        )}
      </Field>

      <Field
        label="Описание"
        error={errors.about}
        counter={`${draft.about.length}/120`}
        anchor={(el) => { anchors.current.about = el; }}
      >
        {(id, bad) => (
          <textarea
            id={id} className="grow" rows={4} value={draft.about} disabled={locked} aria-invalid={bad}
            onChange={(e) => { patch({ about: e.target.value }); clearError('about'); }}
            onBlur={(e) => check('about', e.target.value)}
          />
        )}
      </Field>

      <Field
        label="Телефон"
        error={errors.phone}
        anchor={(el) => { anchors.current.phone = el; }}
      >
        {(id, bad) => (
          <input
            id={id} type="tel" inputMode="tel" placeholder="+7 (999) 999-99-99"
            value={draft.phone} disabled={locked} aria-invalid={bad}
            onChange={(e) => { patch({ phone: maskPhone(e.target.value) }); clearError('phone'); }}
            onBlur={(e) => check('phone', e.target.value)}
          />
        )}
      </Field>

      <Field label="Электронная почта" error={errors.email} anchor={(el) => { anchors.current.email = el; }}>
        {(id, bad) => (
          <input
            id={id} type="email" inputMode="email" autoCapitalize="off" spellCheck={false}
            value={draft.email} disabled={locked} aria-invalid={bad}
            onChange={(e) => { patch({ email: e.target.value }); clearError('email'); }}
            onBlur={(e) => check('email', e.target.value.trim())}
          />
        )}
      </Field>

      <Field label="Сайт" error={errors.site} anchor={(el) => { anchors.current.site = el; }}>
        {(id, bad) => (
          <input
            id={id} type="url" inputMode="url" autoCapitalize="off" spellCheck={false}
            value={draft.site} disabled={locked} aria-invalid={bad}
            onChange={(e) => { patch({ site: e.target.value }); clearError('site'); }}
            onBlur={(e) => {
              // Схему дописываем на уходе из поля: её забывают все, а ошибка
              // ради одного «https://» — плохой обмен.
              const site = normalizeSite(e.target.value);
              patch({ site });
              check('site', site);
            }}
          />
        )}
      </Field>

      {/* Реквизиты живут под выключенным флагом: онлайн-оплата не подключена,
          поля рисуются, но не работают. Место у них здесь — это тоже профиль
          организации, только для чеков, а не для клиента. */}
      {data.data?.features.payments ? (
        <>
          <SectionHead title="Реквизиты" note="Печатаем в чеках при онлайн-оплате" />
          <Field label="Юридическое название" hint="Появится, когда включим онлайн-оплату">
            {(id) => <input id={id} type="text" value="" disabled readOnly />}
          </Field>
          <Field label="ИНН">{(id) => <input id={id} type="text" value="" disabled readOnly />}</Field>
          <Field label="Адрес для чеков">{(id) => <input id={id} type="text" value="" disabled readOnly />}</Field>
        </>
      ) : null}

      {writable ? (
        <StickySave dirty={dirty} busy={busy} failed={save.isError} onClick={() => { void submit().catch(() => {}); }} />
      ) : null}
    </Screen>
  );
}
